import { editorEnum } from 'src/model/editor/editorEnum'
import {
  formatPrice,
  getPaymentFrequency,
} from '../../simple-box-wrapper-options/utils'
import {
  isDefaultHeaderImage,
  primaryHeaderBackground,
} from 'src/utils/default-header-background'
import { resolveFont, resolvePrimaryColor } from 'src/utils/workspace-logo'

export const headerHeroTemplate = (
  landingPageData,
  course,
  collection,
  pageUrl,
  collectionSettings,
  collectionGeneralSettings,
  certificatePayment,
) => {
  if (landingPageData) {
    // A page on the default color shows the workspace branding's (buttons,
    // links and the title too), as learners see it.
    landingPageData = {
      ...landingPageData,
      primaryColor: resolvePrimaryColor(landingPageData.primaryColor),
    }
    // The same URL on every render: a cache-busting timestamp made the browser
    // download the logo again each time, so the header jumped while it loaded.
    // A new logo is a new file, so a stale one can't show.
    const logoUrl = landingPageData.logo
      ? landingPageData.logo.startsWith('https')
        ? landingPageData.logo
        : `https://cdn.minicoursegenerator.com/${landingPageData.logo}`
      : ''

    // Get background image URL
    const landingPageSection = landingPageData?.sections?.find(
      x => x.type === editorEnum.headerType,
    )
    const backgroundImageUrl =
      landingPageData &&
        landingPageData.sections &&
        landingPageSection &&
        landingPageSection.content &&
        landingPageSection.content.image
        ? landingPageSection.content.image.startsWith('https')
          ? landingPageSection.content.image
          : 'https://cdn.minicoursegenerator.com/' +
          landingPageSection.content.image
        : '/default-image.jpg'

    // Get course/collection data
    const title =
      pageUrl === 'mini-course'
        ? course?.name || 'Course Title'
        : collection?.name || 'Learning Path Title'

    // Get description from settings instead of course/collection data
    const description =
      landingPageSection?.content?.description ||
      (pageUrl === 'mini-course'
        ? course?.description || ''
        : collection?.description || '')

    // Get alignment setting for hero layout
    const alignment = landingPageSection?.content?.alignment || 'left'
    const isLeftAligned = alignment === 'left'

    // Set flex direction based on alignment
    const flexDirection = isLeftAligned ? 'row' : 'row-reverse'

    return `
      <div class="hero-template-container editor-header-container" style="background-color: ${landingPageSection?.content?.backgroundColor || '#FFFFFF'
      } !important">
        <!-- Main Hero Content -->
        <div class="hero-main-content" style="display: flex !important; flex-direction: ${flexDirection} !important; align-items: center !important; height: 100% !important;">
          <!-- Left Content -->
          <div class="hero-left-content" style="display: flex !important; flex-direction: column !important; justify-content: space-between !important; height: 100% !important;">
            <!-- Content Section -->
            <div class="hero-content-section">
              <!-- Logo Section -->
              ${landingPageData.logo
        ? `<div class="hero-logo-container">
                      <img 
                        src="${logoUrl}" 
                        class="hero-logo" 
                        alt="logo" 
                      />
                    </div>`
        : ''
      }
              <h1 class="hero-title" style="font-family: ${resolveFont(landingPageData.font)
      } !important; color: ${landingPageSection?.content?.textColor || '#002E47'
      } !important; display: -webkit-box !important; -webkit-line-clamp: 2 !important; -webkit-box-orient: vertical !important; overflow: hidden !important; text-overflow: ellipsis !important;">${title}</h1>
              
              <p class="hero-description" style="font-family: ${resolveFont(landingPageData.font)
      } !important; color: ${landingPageSection?.content?.textColor || '#002E47'
      } !important; display: ${description ? '-webkit-box' : 'none'
      } !important;">${description || ''
      }</p>
            </div>
            
            <!-- Action Section - Pushed to bottom -->
            <div class="hero-action-section">
              ${(() => {
        // Get pricing data from landing page settings
        const priceTagActive =
          landingPageSection?.content?.priceTagActive

        let buttonsHtml = ''

        // Check if it's a showcase collection (type === 1)
        const isShowcaseCollection = collection?.type === 1

        // Start Course button - show for courses and non-showcase collections
        if (pageUrl === 'mini-course' || !isShowcaseCollection) {
          buttonsHtml += `
                    <button class="hero-start-course-btn" style="background-color: ${landingPageData.primaryColor || '#001B47'
            } !important; font-family: ${resolveFont(landingPageData.font)
            } !important; font-size: 16px !important; font-weight: 500 !important; color: white !important; border: none !important; padding: 8px 16px !important; border-radius: 8px !important; height: 44px;">
                      ${landingPageData?.localizations?.startButton ||
            (pageUrl === 'mini-course'
              ? 'Start Course'
              : 'Get Started')
            }
                    </button>
                  `
        }

        // See More button - for both courses and collections (outlined)
        buttonsHtml += `
                  <button class="hero-see-more-btn" style="background-color: transparent !important; color: ${landingPageData.primaryColor || '#001B47'
          } !important; font-family: ${resolveFont(landingPageData.font)
          } !important; font-size: 16px !important; font-weight: 500 !important; height: 44px; border: 2px solid ${landingPageData.primaryColor || '#001B47'
          } !important; padding: 8px 16px !important; border-radius: 8px !important;">
                    ${landingPageData?.localizations
            ? landingPageData.localizations.readMore || 'See more'
            : 'See more'
          }
                  </button>
                `

        // Paid certificate indicator — mirrors the learner-app pill
        // (mcg-frontend-app `certificate-indicator/index.tsx`). Shows the
        // passive "Verified Certificate Available" form only; no CTA, since
        // the editor has no learner session.
        if (
          certificatePayment?.isConfigured &&
          certificatePayment.amount &&
          pageUrl !== 'mini-course'
        ) {
          const accent = landingPageData.primaryColor || '#015FFB'
          const fontFamily = resolveFont(landingPageData.font)
          const certFreq = getPaymentFrequency(certificatePayment).label
          const certPrice = formatPrice(certificatePayment)
          buttonsHtml += `
                  <div class="certificate-indicator" style="display: flex !important; align-items: center !important; justify-content: center !important; gap: 8px !important; padding: 8px 16px !important; height: 44px !important; box-sizing: border-box !important; border-radius: 8px !important; border: 1px solid #E0E5EB !important; background-color: #FFFFFF !important; font-size: 16px !important; font-family: ${fontFamily} !important; color: #212B36 !important;">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="${accent}" style="flex-shrink: 0;"><path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                    <span>Verified Certificate Available: <strong style="color: ${accent}; font-weight: 600;">${certPrice}</strong>${certFreq ? `<span style="color: #637381; font-weight: 400;"> ${certFreq}</span>` : ''}</span>
                  </div>
                `
        }

        // Pricing - only for collections
        if (pageUrl !== 'mini-course' && collection.type !== 1) {
          buttonsHtml += `
                    <div class="course-container-header-price" style="font-family: ${resolveFont(landingPageData.font)
            } !important; display: ${priceTagActive ? 'block' : 'none'
            }">
                          <p><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M17.4703 7.5803L11.0503 1.1703C10.8868 1.00581 10.6711 0.903291 10.4403 0.880297L1.0903 0.000296701C0.943829 -0.0131743 0.796192 0.00583103 0.657911 0.0559579C0.51963 0.106085 0.39411 0.186099 0.290297 0.290297C0.186099 0.39411 0.106085 0.51963 0.0559579 0.657911C0.00583103 0.796192 -0.0131743 0.943829 0.000296701 1.0903L0.880297 10.4403C0.903291 10.6711 1.00581 10.8868 1.1703 11.0503L7.5803 17.4703C7.92409 17.8094 8.38741 17.9997 8.8703 18.0003C9.35015 17.9993 9.81018 17.8088 10.1503 17.4703L17.4703 10.1503C17.8104 9.80914 18.0014 9.34704 18.0014 8.8653C18.0014 8.38355 17.8104 7.92145 17.4703 7.5803ZM7.5603 7.5803C7.35059 7.79026 7.08332 7.93331 6.7923 7.99134C6.50128 8.04937 6.19958 8.01977 5.92538 7.90629C5.65118 7.79282 5.41681 7.60056 5.2519 7.35385C5.087 7.10713 4.99898 6.81705 4.99898 6.5203C4.99898 6.22355 5.087 5.93346 5.2519 5.68675C5.41681 5.44003 5.65118 5.24778 5.92538 5.1343C6.19958 5.02082 6.50128 4.99123 6.7923 5.04926C7.08332 5.10728 7.35059 5.25033 7.5603 5.4603C7.83754 5.73943 7.99313 6.11688 7.99313 6.5103C7.99313 6.90371 7.83754 7.28116 7.5603 7.5603V7.5803Z" fill="${landingPageData.primaryColor || '#015FFB'
            }"/>
                    </svg> ${collectionGeneralSettings?.price &&
              collectionGeneralSettings?.accessRule == 4
              ? formatPrice(collectionGeneralSettings.price) +
              `<span class="course-container-price-title">${getPaymentFrequency(collectionGeneralSettings.price)
                .label
              }</span>`
              : 'Free'
            }</</p>
                          </div>
                  `
        }

        return buttonsHtml
      })()}
            </div>
          </div>

          <!-- Right Hero Image -->
          <div class="hero-image-container" style="background: ${
            isDefaultHeaderImage(backgroundImageUrl)
              ? primaryHeaderBackground(landingPageData?.primaryColor)
              : `linear-gradient(0deg, rgba(0, 0, 0, 0.02) 0%, rgba(0, 0, 0, 0.02) 100%), url(${backgroundImageUrl}) lightgray 50% / cover no-repeat`
          };">
            <div class="hero-image-placeholder">
              <!-- Background image is applied to the container -->
              <div class="hero-image-content">
                <div class="hero-image-shape"></div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    `
  }
  return ''
}
